import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  UserCheck,
  Crown,
  Sunrise,
  TrendingUp,
  CheckCircle2,
  Send,
  Search,
  AlertTriangle,
  KeyRound,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MenuSheet from "@/components/MenuSheet";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

function StatTile({ icon: Icon, label, value, hint, testId }) {
  return (
    <Card className="rounded-2xl" data-testid={testId}>
      <CardContent className="pt-5">
        <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider">
          <Icon strokeWidth={1.5} className="size-4 text-primary" />
          {label}
        </div>
        <div className="font-display text-4xl font-extrabold mt-1.5 tabular-nums" data-testid={`${testId}-value`}>
          {value ?? "—"}
        </div>
        {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
      </CardContent>
    </Card>
  );
}

function Sparkline({ data }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(1, ...data.map((d) => d.count));
  const W = 280;
  const H = 60;
  const stepX = W / Math.max(1, data.length - 1);
  const points = data.map((d, i) => `${(i * stepX).toFixed(1)},${(H - (d.count / max) * H).toFixed(1)}`);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-16" preserveAspectRatio="none" data-testid="signups-sparkline">
      <polyline
        points={points.join(" ")}
        fill="none"
        stroke="hsl(var(--primary))"
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {data.map((d, i) => (
        <circle
          key={d.date}
          cx={(i * stepX).toFixed(1)}
          cy={(H - (d.count / max) * H).toFixed(1)}
          r="2"
          fill="hsl(var(--primary))"
        />
      ))}
    </svg>
  );
}

export default function AdminStatsPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionEmail, setActionEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const loadStats = async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (e) {
      toast.error("Could not load stats", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const verifyEmail = async (email) => {
    setBusy(true);
    try {
      await api.post("/admin/verify-user-email", { email });
      toast.success(`Verified ${email}`);
      await loadStats();
    } catch (e) {
      toast.error("Could not verify", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setBusy(false);
    }
  };

  const resendVerification = async (email) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/resend-verification", { email });
      if (data.already_verified) {
        toast.success(`${email} is already verified`);
      } else {
        toast.success(`Verification email re-sent to ${email}`);
      }
    } catch (e) {
      toast.error("Could not resend", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setBusy(false);
    }
  };

  const unlockAccount = async (identifier) => {
    setBusy(true);
    try {
      await api.post("/admin/unlock-account", { identifier });
      toast.success(`Unlocked ${identifier}`);
      await loadStats();
    } catch (e) {
      toast.error("Could not unlock", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setBusy(false);
    }
  };

  if (!user || user.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="rounded-2xl max-w-md text-center">
          <CardContent className="pt-6 space-y-3">
            <div className="font-display text-xl font-bold">Admin only</div>
            <p className="text-sm text-muted-foreground">This page is restricted.</p>
            <Button asChild className="rounded-xl">
              <Link to="/">Back to planner</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8 space-y-5" data-testid="admin-stats-page">
        <div className="flex items-center gap-2">
          <MenuSheet />
          <Button variant="ghost" size="sm" asChild className="rounded-xl" data-testid="admin-stats-back">
            <Link to="/">
              <ArrowLeft strokeWidth={1.5} className="size-4 mr-1.5" />
              Back to planner
            </Link>
          </Button>
        </div>

        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Admin
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-0.5">
            User statistics
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Snapshot of who&apos;s using Lumivera right now.
          </p>
        </div>

        {loading ? (
          <Card className="rounded-2xl">
            <CardContent className="pt-6 text-sm text-muted-foreground">Loading…</CardContent>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatTile
                icon={Users}
                label="Total users"
                value={stats?.total_users}
                hint={`${stats?.verified_users ?? 0} verified`}
                testId="stat-total"
              />
              <StatTile
                icon={UserCheck}
                label="Signups today"
                value={stats?.signups_today}
                hint={`${stats?.signups_24h ?? 0} in last 24h`}
                testId="stat-today"
              />
              <StatTile
                icon={TrendingUp}
                label="Last 7 days"
                value={stats?.signups_7d}
                hint={`${stats?.signups_30d ?? 0} in last 30d`}
                testId="stat-7d"
              />
              <StatTile
                icon={Crown}
                label="Premium"
                value={stats?.premium_users}
                hint={`${stats?.trialing_users ?? 0} on free trial`}
                testId="stat-premium"
              />
            </div>

            <Card className="rounded-2xl">
              <CardHeader>
                <CardTitle className="text-lg font-display">Signups · last 14 days</CardTitle>
                <CardDescription>
                  Hover not required — every dot is one day. Max value scales automatically.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Sparkline data={stats?.by_day} />
                <div className="flex justify-between text-[10px] text-muted-foreground mt-1 font-mono">
                  <span>{stats?.by_day?.[0]?.date}</span>
                  <span>{stats?.by_day?.[stats.by_day.length - 1]?.date}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl" data-testid="admin-user-actions">
              <CardHeader>
                <CardTitle className="text-lg font-display flex items-center gap-2">
                  <Search strokeWidth={1.5} className="size-5 text-primary" />
                  Help a user verify
                </CardTitle>
                <CardDescription>
                  If a user can&apos;t receive the verification email (spam, typo, dead address), enter their email and either resend the link or mark them verified manually.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-stretch gap-2">
                  <Input
                    type="email"
                    value={actionEmail}
                    onChange={(e) => setActionEmail(e.target.value)}
                    placeholder="user@example.com"
                    className="flex-1 min-w-[200px] rounded-xl"
                    data-testid="admin-action-email-input"
                  />
                  <Button
                    variant="outline"
                    onClick={() => actionEmail && resendVerification(actionEmail.trim().toLowerCase())}
                    disabled={busy || !actionEmail.trim()}
                    className="rounded-xl"
                    data-testid="admin-resend-button"
                  >
                    <Send strokeWidth={1.5} className="size-4 mr-1.5" />
                    Resend email
                  </Button>
                  <Button
                    onClick={() => actionEmail && verifyEmail(actionEmail.trim().toLowerCase())}
                    disabled={busy || !actionEmail.trim()}
                    className="rounded-xl"
                    data-testid="admin-verify-button"
                  >
                    <CheckCircle2 strokeWidth={1.5} className="size-4 mr-1.5" />
                    Mark verified
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">
                  &quot;Mark verified&quot; bypasses email — use only when you&apos;ve separately confirmed the person is who they say they are.
                </p>
              </CardContent>
            </Card>

            {/* Users who need help — surfaces automatically */}
            {(stats?.stuck_signups?.length > 0 || stats?.locked_accounts?.length > 0) && (
              <Card
                className="rounded-2xl border-2 border-amber-400/50 bg-amber-50/30 dark:bg-amber-950/20"
                data-testid="admin-needs-help"
              >
                <CardHeader>
                  <CardTitle className="text-lg font-display flex items-center gap-2">
                    <AlertTriangle strokeWidth={1.5} className="size-5 text-amber-600" />
                    Users needing help
                  </CardTitle>
                  <CardDescription>
                    Auto-detected accounts that signed up but never verified after 6+ hours, or that
                    are currently locked out from too many failed login attempts.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {stats?.stuck_signups?.length > 0 && (
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                        Stuck signups · {stats.stuck_signups.length}
                      </div>
                      <ul className="space-y-1.5" data-testid="stuck-signups-list">
                        {stats.stuck_signups.map((u) => (
                          <li
                            key={u.email}
                            className="flex items-center justify-between gap-2 flex-wrap rounded-lg bg-background/60 px-3 py-2"
                            data-testid={`stuck-${u.email}`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium truncate">{u.email}</div>
                              <div className="text-[11px] text-muted-foreground">
                                Unverified for {u.age_days ?? "?"} day{u.age_days === 1 ? "" : "s"}
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => resendVerification(u.email)}
                                disabled={busy}
                                className="rounded-lg h-7 px-2 text-[11px]"
                                data-testid={`stuck-resend-${u.email}`}
                              >
                                Resend
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => verifyEmail(u.email)}
                                disabled={busy}
                                className="rounded-lg h-7 px-2 text-[11px]"
                                data-testid={`stuck-verify-${u.email}`}
                              >
                                Verify
                              </Button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {stats?.locked_accounts?.length > 0 && (
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1">
                        <KeyRound strokeWidth={1.5} className="size-3" />
                        Locked out · {stats.locked_accounts.length}
                      </div>
                      <ul className="space-y-1.5" data-testid="locked-accounts-list">
                        {stats.locked_accounts.map((a) => (
                          <li
                            key={a.identifier}
                            className="flex items-center justify-between gap-2 flex-wrap rounded-lg bg-background/60 px-3 py-2"
                            data-testid={`locked-${a.identifier}`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium truncate">{a.identifier}</div>
                              <div className="text-[11px] text-muted-foreground">
                                Locked for {a.minutes_remaining ?? "?"} more min
                              </div>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => unlockAccount(a.identifier)}
                              disabled={busy}
                              className="rounded-lg h-7 px-2 text-[11px]"
                              data-testid={`unlock-${a.identifier}`}
                            >
                              Unlock
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <Card className="rounded-2xl">
              <CardHeader>
                <CardTitle className="text-lg font-display flex items-center gap-2">
                  <Sunrise strokeWidth={1.5} className="size-5 text-primary" />
                  Recent signups
                </CardTitle>
                <CardDescription>The 10 most recent accounts.</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="divide-y divide-border" data-testid="admin-recent-users">
                  {(stats?.recent_users || []).map((u) => (
                    <li
                      key={u.email}
                      className="py-2.5 flex items-center justify-between gap-3 flex-wrap"
                      data-testid={`recent-user-${u.email}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm truncate">{u.name || u.email}</div>
                        <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                      </div>
                      <div className="flex items-center gap-2 text-[11px] flex-wrap">
                        {u.verified ? (
                          <span className="px-2 py-0.5 rounded-full bg-primary/15 text-primary font-semibold">
                            verified
                          </span>
                        ) : (
                          <>
                            <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                              unverified
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => resendVerification(u.email)}
                              disabled={busy}
                              className="rounded-lg h-7 px-2 text-[11px]"
                              data-testid={`resend-${u.email}`}
                            >
                              Resend
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => verifyEmail(u.email)}
                              disabled={busy}
                              className="rounded-lg h-7 px-2 text-[11px]"
                              data-testid={`verify-${u.email}`}
                            >
                              Verify
                            </Button>
                          </>
                        )}
                        {u.subscription_status && (
                          <span className="px-2 py-0.5 rounded-full bg-accent text-accent-foreground font-semibold">
                            {u.subscription_status}
                          </span>
                        )}
                        <span className="text-muted-foreground font-mono">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}
                        </span>
                      </div>
                    </li>
                  ))}
                  {(stats?.recent_users || []).length === 0 && (
                    <li className="py-4 text-center text-sm text-muted-foreground">No users yet.</li>
                  )}
                </ul>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
